import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export interface Crumb { label: string; to?: string }

export function PageHeader({ title, sub, crumbs, actions, badge }: {
  title: ReactNode; sub?: ReactNode; crumbs?: Crumb[]; actions?: ReactNode; badge?: ReactNode;
}) {
  return (
    <header className="page-head">
      {crumbs && crumbs.length > 0 && (
        <nav className="crumbs" aria-label="Breadcrumb">
          {crumbs.map((c, i) => (
            <span key={i}>{c.to ? <Link to={c.to}>{c.label}</Link> : c.label}<span aria-hidden="true"> / </span></span>
          ))}
        </nav>
      )}
      <div className="page-head-row">
        <div>
          <h1>{title} {badge}</h1>
          {sub && <p className="sub">{sub}</p>}
        </div>
        {actions && <div className="actions">{actions}</div>}
      </div>
    </header>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <p className="muted loading" role="status">{label}</p>;
}

export function ErrorBox({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div className="error-box" role="alert">
      <span className="issue-badge is-error" aria-hidden="true">✕</span>
      <span>{error}</span>
      {onRetry && <button className="btn btn-sm" onClick={onRetry}>Retry</button>}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function Card({ title, children, actions, className }: {
  title?: ReactNode; children: ReactNode; actions?: ReactNode; className?: string;
}) {
  return (
    <section className={`card${className ? ` ${className}` : ""}`}>
      {(title || actions) && (
        <div className="card-head">{title && <h2>{title}</h2>}{actions}</div>
      )}
      {children}
    </section>
  );
}

/** Label/value pairs, for detail pages. */
export function Facts({ items }: { items: [string, ReactNode][] }) {
  return (
    <dl className="facts">
      {items.map(([k, v]) => (<div key={k}><dt>{k}</dt><dd>{v ?? "—"}</dd></div>))}
    </dl>
  );
}
